<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.title.addNewTask')"
    dialog-width="29%"
    agree-text="dialog.agreeSave"
    header-shadow
    :is-valid="isValid"
    @agree="handleTaskAdding"
    @modalClosed="onClose"
    :loading="isLoading"
  >
    <template #body>
      <v-col cols="12">
        <v-form v-model="isValid" autocomplete="off" ref="form">
          <sidebar-form v-model="payload" :inputs="inputs" is-editing-on :is-valid="isValid">
            <template #status="scope">
              <sidebar-status-select v-bind="scope" v-model="payload.status" />
            </template>
            <template #assigned_user="scope">
              <user-select v-bind="scope" v-model="payload.assigned_user" />
            </template>
          </sidebar-form>
        </v-form>
      </v-col>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { getPolygonFromBbox } from '@/assets/js/utility';
import SidebarStatusSelect from '@/components/SidebarStatusSelect';
import UserSelect from '@/components/UserSelect';
import SidebarForm from '@/components/SidebarForm';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'QuickTask',
  components: {
    SidebarForm,
    MainLargeDialog,
    UserSelect,
    SidebarStatusSelect,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    selectedFeatures: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    payload: {},
    isLoading: false,
    isValid: false,
  }),
  computed: {
    statuses: get('admin/modulesMapping@tasks.statuses'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    tasksCategories: get('tasks/tasksCategories'),
    tasksLayerId: get('admin/modulesMapping@tasks.layer_id'),
    tasksObjects: get('tasks/tasksObjects'),
    users: get('users/users'),
    currentUser: get('users/currentUser'),
    inputs() {
      return [
        {
          name: 'category',
          translationPath: 'dialog.category',
          cols: '8',
          icon: 'mdi-tag-multiple',
          dataType: { name: 'select' },
          items: this.tasksCategories,
          itemText: 'name',
          itemValue: 'name',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'status',
          divided: true,
          translationPath: 'dialog.status',
          cols: 'auto',
          icon: 'mdi-progress-check',
          isCustom: true,
          type: 'tasks',
          rules: [v => !!v || v == '0' || 'rules.required'],
          colors: {
            new: {
              color: 'rgba(0,145,234)',
              shadowColor: 'rgba(0,145,234,0.2)',
            },
            done: {
              color: 'rgba(24,128,56)',
              shadowColor: 'rgba(24,128,56,0.2)',
            },
            in_progress: {
              color: 'rgba(255,136,0)',
              shadowColor: 'rgba(255,136,0,0.2)',
            },
            planned: {
              color: 'rgba(108,108,108)',
              shadowColor: 'rgba(108,108,108,0.2)',
            },
          },
        },
        {
          name: 'name',
          translationPath: 'dialog.name',
          icon: '$label',
          cols: '8',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'description',
          translationPath: 'dialog.description',
          cols: '8',
          icon: 'mdi-information',
          dataType: { name: 'textarea' },
          autoGrow: true,
          divided: true,
          dense: true,
          rows: '1',
        },
        {
          name: 'assigned_user',
          isCustom: true,
          translationPath: 'dialog.assignedPerson',
          cols: '8',
          value: '',
          icon: 'mdi-account-arrow-left',
        },
        {
          name: 'due_date',
          translationPath: 'dialog.timeLimit',
          cols: '8',
          icon: 'mdi-calendar',
          dataType: { name: 'date' },
        },
      ];
    },
  },
  methods: {
    addTask: call('tasks/addTask'),
    getTasksCategories: call('tasks/getTasksCategories'),
    getTaskObjects: call('tasks/getTaskObjects'),
    editTaskObjects: call('tasks/editTaskObjects'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    async handleTaskAdding() {
      this.isLoading = true;
      try {
        const features = await this.getLayerFeatures({
          layer_id: this.selectedFeatures.layerId,
          features_filter: {
            $IN: {
              [`${this.selectedFeatures.dataSource}.id`]: this.selectedFeatures.featuresIds,
            },
          },
          params: { with_collection_bbox: true, with_features: false, with_count: false },
        });
        const bbox = features.data.data.bbox;
        const polygon = getPolygonFromBbox(bbox, 50, 50);
        const geometry = {
          type: 'Polygon',
          coordinates: [polygon],
          crs: {
            properties: {
              name: this.$_config.defaultEpsg,
            },
            type: 'name',
          },
        };
        const dateNow = new Date();
        const dateNowIsoString = dateNow.toISOString();
        const data = {
          ...this.payload,
          reported_by: this.currentUser.id,
          reported_at: dateNowIsoString.slice(0, -1),
          geometry,
        };
        const r = await this.addTask(data);
        this.$root.$emit('refreshNavbarInfoButtons', ['tasks']);
        const selectedObjects = this.selectedFeatures.featuresIds.map(value => {
          return { layer_id: this.selectedFeatures.layerId, object_id: value };
        });
        const objects = {
          task_id: r.data.data,
          data: { add: selectedObjects, delete: [] },
        };
        await this.editTaskObjects(objects);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.taskAddingSuccess'),
        });
        this.computedIsVisible = false;
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.taskAddingFailed'),
        });
      } finally {
        this.isLoading = false;
        this.onClose();
      }
    },
    onClose() {
      this.payload = {};
      this.payload.status = this.statuses.done;
      this.$refs.form.resetValidation();
    },
    async init() {
      await Promise.all([this.getTasksCategories()]);
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped></style>
